import AF from '../utlis/AF';
import ClickCursor from '../components/click-cursor';
// import * as easing from '~/scripts/utils/ease-adv';
import bezier from '../utlis/bezier-easing';
import { getX, getY, getType } from '../utlis/slider-helpers';
import { isMobile } from '../utlis/is-mobile';

const CLASS_ACTIVE = 'is-active';
const CLASS_PREV = 'is-prev';
const CLASS_TRANSITION = 'is-transition';

export default class Slider {
    constructor(element) {
        this.el = element;
        if (!this.el) return;

        this.index = 0;
        this.pos = 0;
        this.indexPrev = null;
        this.resizeTimer = null;
        this.autoslideTimer = null;
        this.time = 10000;
        this.inview = false;

        this.af = AF.instance();
        this.clickCursor = ClickCursor.instance();

        this.touch = {
            disabled: true,
            down: false,
            dragging: false,
            moved: false,
            type: '',
            xStart: 0,
            yStart: 0,
            xDist: 0,
            yDist: 0
        };

        this.init();
    }

    init() {
        this.bindMethods();
        this.setupElements();
        this.attachEventListeners();

        if (document.readyState === 'complete') {
            this.run();
        } else {
            window.addEventListener('load', this.run);
        }
    }

    bindMethods() {
        this.run = this.run.bind(this);
        this.goNext = this.goNext.bind(this);
        this.goBack = this.goBack.bind(this);
        this.tick = this.tick.bind(this);
        this.render = this.render.bind(this);
        this.onResize = this.onResize.bind(this);
        this.onOverNext = this.onOverNext.bind(this);
        this.onOverBack = this.onOverBack.bind(this);
        this.onOut = this.onOut.bind(this);
        this.onTouchDown = this.onTouchDown.bind(this);
        this.onTouchMove = this.onTouchMove.bind(this);
        this.onTouchUp = this.onTouchUp.bind(this);
        this.move = this.move.bind(this);
        this.read = this.read.bind(this);
    }

    setupElements() {
        this.frame = this.el.querySelector('[frame]');
        this.rect = this.frame.getBoundingClientRect();
        this.frameWrap = this.el.querySelector('[frame-wrap]');
        this.clickableNext = this.el.querySelector('[frame-clickable-next]');
        this.clickableBack = this.el.querySelector('[frame-clickable-back]');
        this.list = this.el.querySelector('[list]');
        this.slides = Array.from(this.el.querySelectorAll('[slide]'));
        this.l = this.slides.length;
        this.bgItems = Array.from(this.el.querySelectorAll('[bg-item]'));
        this.conItems = Array.from(this.el.querySelectorAll('[content-item]'));
        this.conTitles = Array.from(this.el.querySelectorAll('[content-title]'));
        this.autoCounterEl = Array.from(this.el.querySelectorAll('[auto-counter]'));
        this.autoCounterItems = Array.from(this.el.querySelectorAll('[auto-counter-item]'));
    }

    attachEventListeners() {
        window.addEventListener('resize', this.onResize);
        this.clickableNext.addEventListener('click', this.goNext);
        this.clickableBack.addEventListener('click', this.goBack);
        this.clickableNext.addEventListener('mouseenter', this.onOverNext);
        this.clickableNext.addEventListener('mouseleave', this.onOut);
        this.clickableBack.addEventListener('mouseenter', this.onOverBack);
        this.clickableBack.addEventListener('mouseleave', this.onOut);

        const touchStartEvent = 'ontouchstart' in document.documentElement ? 'touchstart' : 'mousedown';
        const touchMoveEvent = 'ontouchmove' in document.documentElement ? 'touchmove' : 'mousemove';
        const touchEndEvent = 'ontouchend' in document.documentElement ? 'touchend' : 'mouseup';

        this.el.addEventListener(touchStartEvent, this.onTouchDown);
        this.el.addEventListener(touchMoveEvent, this.onTouchMove);
        window.addEventListener(touchEndEvent, this.onTouchUp);

        this.autoCounterItems.forEach((item, i) =>
            item.addEventListener('click', this.onClick.bind(this, i - 1))
        );
    }

    detachEventListeners() {
        window.removeEventListener('resize', this.onResize);
        this.clickableNext.removeEventListener('click', this.goNext);
        this.clickableBack.removeEventListener('click', this.goBack);
        this.clickableNext.removeEventListener('mouseenter', this.onOverNext);
        this.clickableNext.removeEventListener('mouseleave', this.onOut);
        this.clickableBack.removeEventListener('mouseenter', this.onOverBack);
        this.clickableBack.removeEventListener('mouseleave', this.onOut);

        const touchStartEvent = 'ontouchstart' in document.documentElement ? 'touchstart' : 'mousedown';
        const touchMoveEvent = 'ontouchmove' in document.documentElement ? 'touchmove' : 'mousemove';
        const touchEndEvent = 'ontouchend' in document.documentElement ? 'touchend' : 'mouseup';

        this.el.removeEventListener(touchStartEvent, this.onTouchDown);
        this.el.removeEventListener(touchMoveEvent, this.onTouchMove);
        window.removeEventListener(touchEndEvent, this.onTouchUp);

        this.autoCounterItems.forEach((item, i) =>
            item.removeEventListener('click', this.onClick.bind(this, i - 1))
        );
    }

    run() {
        this.af.onNextRead(this.tick);
        this.list.classList.add(CLASS_TRANSITION);

        if (!isMobile) {
          this.af.addRead(this.read);
          // Start autoplay immediately
          clearTimeout(this.autoslideTimer);
          this.autoslideTimer = setTimeout(this.goNext, this.time);
        }
    }

    goNext() {
        if (!this.touch.disabled) {
            return;
        }
        let index = (this.index + 1) % this.l;
        this.select(index);
    }

    goBack() {
        if (!this.touch.disabled) {
            return;
        }
        let index = (this.index - 1 + this.l) % this.l;
        this.select(index);
    }

    select(index) {
        if (index !== this.index) {
            this.indexPrev = this.index;
            this.index = index;
        }

        if (!isMobile) {
            clearTimeout(this.autoslideTimer);
            this.autoslideTimer = setTimeout(this.goNext, this.time);
        }

        this.af.onNextWrite(this.render);
    }

    render() {
        this.bgItems.forEach((item, i) => {
            item.classList.toggle(CLASS_ACTIVE, i === this.index);
            item.classList.toggle(CLASS_PREV, i === this.indexPrev);
        });

        this.slides.forEach((item, i) => {
            item.classList.toggle(CLASS_ACTIVE, i === this.index);
        });

        this.conItems.forEach((item, i) => {
            item.classList.toggle(CLASS_ACTIVE, i === this.index);
        });

        this.conTitles.forEach((item, i) => {
            item.classList.toggle(CLASS_ACTIVE, i === this.index);
        });

        this.autoCounterItems.forEach((item, i) => {
            item.classList.toggle(CLASS_ACTIVE, i === this.index);
        });

        this.pos = -this.rect.width * this.index;
        this.list.style.transform = `translate3d(${this.pos}px, 0, 0)`;
    }



    // Add other helper methods like `onOverNext`, `onOverBack`, `onOut`, `onTouchDown`, `onTouchMove`, etc.

    onResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
          this.rect = this.frame.getBoundingClientRect();
          this.render();
      }, 200);
  }
  
  onOverNext() {
    if (!this.touch.disabled) return;
    //console.log('onOverNext');
    this.clickCursor.activate('next');
    // this.clickCursor.show(true); // Enable 'next' state
  }
  
  onOverBack() {
    if (!this.touch.disabled) return;
    this.clickCursor.activate('back');
  }
  
  onOut() {
    if (!this.touch.disabled) return;
    this.clickCursor.deactivate();
  }
  
  onTouchDown(event) {
      if (this.touch.disabled || !('ontouchstart' in document.documentElement)) return;
      this.touch.down = true;
      this.touch.dragging = false;
      this.touch.type = getType(event);
      this.touch.xStart = getX(event, type);
      this.touch.yStart = getY(event, type);
      this.touch.xDist = 0;
      this.touch.yDist = 0;
  
      this.list.classList.remove(CLASS_TRANSITION);
      clearTimeout(this.autoslideTimer);
  }
  
  onTouchMove(event) {
      if (!this.touch.down) return;
  
      const x = getX(event, this.touch.type);
      const y = getY(event, this.touch.type);
  
      this.touch.xDist = x - this.touch.xStart;
      this.touch.yDist = y - this.touch.yStart;
  
      if (!this.touch.dragging && Math.abs(this.touch.xDist) > Math.abs(this.touch.yDist)) {
          this.touch.dragging = true;
      }
  
      if (this.touch.dragging) {
          event.preventDefault();
          this.move(this.pos + this.touch.xDist);
      }
  }
  
  onTouchUp() {
      if (!this.touch.down) return;
  
      this.touch.down = false;
  
      if (this.touch.dragging) {
          this.touch.dragging = false;
  
          if (Math.abs(this.touch.xDist) > this.rect.width / 4) {
              if (this.touch.xDist > 0) {
                  this.goBack();
              } else {
                  this.goNext();
              }
          } else {
              this.select(this.index); // Snap back to the current slide
          }
      } else {
          this.select(this.index); // No dragging, snap to the current slide
      }
  
      if (!isMobile) {
          this.autoslideTimer = setTimeout(this.goNext, this.time);
      }
  
      this.list.classList.add(CLASS_TRANSITION);
  }
  
  move(position) {
      this.list.style.transform = `translate3d(${position}px, 0, 0)`;
  }
  
  tick() {
      this.inview = true; // You can add additional logic for viewport checks if needed
      this.af.addWrite(this.render);
  }
  
  read() {
      // Placeholder for custom logic if needed, e.g., viewport checks
  }

  onClick(index) {
    // Ensure the index is within the valid range
    const validIndex = Math.max(0, Math.min(index, this.l - 1));
    
    // If the clicked index is different from the current index, select the new index
    if (validIndex !== this.index) {
        this.select(validIndex);
    }
  }

  

}